import React, { useMemo } from 'react'
import classnames from 'classnames/bind'
import _ from 'lodash'
import * as graph from '@adc/sdk'
// @ts-ignore no types
import type {
  CurrentTargetAudience,
  Listing,
  Maybe,
  OnlineAuctionSegment,
  OnlineAuctionSegmentBidRank,
  ClosingMethod,
  Party,
} from '@adc/sdk/types'
import instrument from '@adc/instrument'
import useBreakpoints from '../../../../hooks/useBreakpoints'
import SummarySection from './summarySection'
import { formatters, traditionalOffer } from '../../../../utils'
import styles from './styles.scss'
import { InfoPillProps } from '../../../elementals/infoPill'
import { ColorNames } from '../../../../constants'
import configment from '@adc/configment'
import { findSellerRepresentative } from '../../../listingSellerRepresentativeCard/utils'
import PhoneVerification from '../../../phoneVerification'
import { NET_OFFER_AMOUNT_TOOLTIP } from '../bidRankList/utils'

const cx = classnames.bind(styles)

interface BidRankSummaryProps {
  bidRank: OnlineAuctionSegmentBidRank
  viewingFrom: CurrentTargetAudience
  listing?: Listing
  segment?: OnlineAuctionSegment
  closingMethod?: ClosingMethod
  embeddedContext?: string
}

const getPhone = (party: Party) => {
  if (!Array.isArray(party?.phones)) return null
  const phone = PhoneVerification.utils.getLatestPhone(party.phones || [])
  return phone ? phone.number : null
}

//hack to get the winning bid info from the segment if the BE still not populating it in the closing method
const getWinningBidUserData = (
  closingMethod: ClosingMethod | undefined,
  segment: any
) => {
  // @ts-ignore
  const buyerParty = closingMethod?.buyers[0]?.buyer_party
  return !!buyerParty
    ? buyerParty
    : {
        ...segment?.result?.winning_bidder_user,
        phone_number: formatters.formatPhoneNumber(
          getPhone(segment?.result?.winning_bidder_user?.party)
        ),
        email_address: segment?.result?.winning_bidder_user?.email,
      }
}

const BidRankSummary = ({
  bidRank,
  viewingFrom = graph.enums.CurrentTargetAudience.Seller,
  listing,
  segment,
  closingMethod,
  embeddedContext = 'browser',
}: BidRankSummaryProps) => {
  const terms = bidRank.bid?.terms
  const breakpoints = useBreakpoints()
  const isMobileOrTablet = breakpoints === 'mobile' || breakpoints === 'tablet'
  // used for targets of links, NOT used for mobile styling, use isMobileOrTablet for styling logic
  const isOnWebView = embeddedContext === 'webview'

  const {
    buyerCommissionsAmount,
    sellerCommissionsAmount,
    sellerConcessionAmount,
  } = traditionalOffer.getOfferTermsCalculations(bidRank.bid)

  const offerNet =
    viewingFrom === graph.enums.CurrentTargetAudience.Buyer
      ? terms?.buyer_net_offer_amount
      : terms?.seller_net_offer_amount

  type Subsection = {
    title?: string
    children: {
      label: string
      value: string | number
      width: string
      href?: string
      level: 1 | 2 | 3 | 4
      isMoneyValue?: boolean
      tooltipContent?: string
      classNames?: any
      type?: 'LABEL' | 'LINE_SEPARATOR' | 'LISTING_AGENT' | 'INFO_PILL'
      className?: string
      hide?: boolean
      infoPill?: InfoPillProps
      viewingFrom?: CurrentTargetAudience
      elmId?: string
    }[]
    className?: string
    tooltip?: string
  }

  type Section = {
    subsections: Subsection[]
    className?: string
  }

  const proofOfFundsDocument = (terms?.documents || []).find(
    d =>
      d.document_purpose_type === graph.enums.UserDocumentPurpose.ProofOfFunds
  )

  const proofOfFundsDocumentURL = proofOfFundsDocument?.urn
    ? `${configment.get('RESI_GRAPH_API_URL')}/download/${
        proofOfFundsDocument.urn
      }`
    : ''

  const proofOfFundsContentType =
    proofOfFundsDocument?.document?.file_descriptor?.content_type || ''

  const valueOrDefault = (
    value: string | number | undefined | Maybe<number>,
    asNegative = false,
    defaultValue = 'N/A'
  ) => {
    return value !== undefined && value !== null
      ? asNegative
        ? `-${value}`
        : value
      : defaultValue
  }

  let commissionPayerType = graph.enums.CommissionPayerType.Seller
  if (bidRank?.bid?.terms?.commission?.length) {
    commissionPayerType = bidRank?.bid?.terms?.commission[0].paid_by
  }

  const offerCalculationParentElmId = 'offer-calculation'

  let leftSections = useMemo<Section[]>(
    () => [
      {
        subsections: [
          {
            title: 'Offer Calculation',
            className: cx('row-children'),
            children: [
              {
                label: 'Purchase Price',
                value: valueOrDefault(terms?.offer_amount),
                width: '100%',
                level: 3,
                isMoneyValue: !!terms?.offer_amount,
                classNames: {
                  label: cx('bold'),
                },
                tooltipContent: `The amount a buyer agrees to pay for a property as stated in the purchase agreement.`,
                elmId: `${offerCalculationParentElmId}-purchase-price`,
              },
              {
                label: 'Marketplace Fee',
                value: valueOrDefault(terms?.market_place_fee_amount, true),
                width: '100%',
                level: 3,
                isMoneyValue: !!terms?.market_place_fee_amount,
                hide: viewingFrom === graph.enums.CurrentTargetAudience.Buyer,
                tooltipContent: `Auction.com charges a marketplace fee to cover listing agent commissions, marketing and technology costs from Auction.com.`,
                elmId: `${offerCalculationParentElmId}-marketplace-fee`,
              },
              {
                label: 'Seller Paid Closing Costs',
                value: valueOrDefault(sellerConcessionAmount, true),
                width: '100%',
                level: 3,
                isMoneyValue: !!sellerConcessionAmount,
                tooltipContent: `The amount the buyer is requesting the seller to contribute towards the buyer's closing costs.`,
                hide: !sellerConcessionAmount,
                elmId: `${offerCalculationParentElmId}-seller-paid-closing-costs`,
              },
              {
                label: "Seller Paid Buyer's Agent Commission",
                value: valueOrDefault(sellerCommissionsAmount, true),
                width: '100%',
                level: 3,
                isMoneyValue: !!sellerCommissionsAmount,
                tooltipContent: `The amount the buyer is asking the seller to pay to the buyer's agent as a commission.`,
                hide:
                  commissionPayerType ===
                    graph.enums.CommissionPayerType.Buyer ||
                  !sellerCommissionsAmount,
                elmId: `${offerCalculationParentElmId}-seller-paid-buyers-agent-commission`,
              },
              {
                label: '',
                value: '',
                width: '100%',
                level: 3,
                type: 'LINE_SEPARATOR',
                className: cx('line-separator'),
              },
              {
                label: 'Net Offer Amount',
                value: valueOrDefault(offerNet),
                width: '100%',
                level: 3,
                isMoneyValue: !!offerNet,
                tooltipContent: NET_OFFER_AMOUNT_TOOLTIP,
                classNames: {
                  label: cx('bold'),
                },
                infoPill: {
                  size: 'medium',
                  elmId: `${offerCalculationParentElmId}-net-offer-amount-value-pill`,
                  backgroundColor: ColorNames.SuccessLight,
                  foregroundColor: ColorNames.Success,
                  includeBorder: false,
                  extraPadding: 'medium',
                  style: {
                    borderRadius: '5px',
                    flexWrap: 'nowrap',
                    marginRight: '-5px',
                    padding: '10px',
                  },
                },
                elmId: `${offerCalculationParentElmId}-net-offer-amount`,
              },
            ],
          },
        ],
      },
    ],
    [bidRank, listing, segment, viewingFrom]
  )

  const nonMobileFluidSections = useMemo<Section[]>(
    () => [
      {
        subsections: [
          {
            title: ` ${
              terms?.notes?.latest_update_reason ===
              graph.enums.NoteUpdateReason.AuctioneerOverwrite
                ? 'Additional Notes - Notes edited due to violation of Website Terms of Use'
                : 'Additional Notes'
            }`,
            children: [
              {
                label: '',
                value: terms?.notes?.additional_notes || '',
                width: '100%',
                level: 3,
                elmId: `additional-notes-field`,
              },
            ],
            tooltip:
              terms?.notes?.latest_update_reason ===
              graph.enums.NoteUpdateReason.AuctioneerOverwrite
                ? `Notes were edited due to a violation of our Website Terms of Use, such as sharing personal information or entering inappropriate content.`
                : '',
          },
        ],
      },
      {
        subsections: [
          {
            title: 'Documents',
            children: [
              {
                type: 'INFO_PILL',
                label: '',
                elmId: `documents-field`,
                value: proofOfFundsDocumentURL,
                width: '50%',
                level: 3,
                hide: !proofOfFundsDocumentURL,
                infoPill: {
                  size: 'medium',
                  elmId: 'proof_of_funds_document',
                  iconName: 'file-pdf',
                  extraPadding: 'medium',
                  fontWeight: 'bold',
                  includeBorder: false,
                  foregroundColor: ColorNames.Gray,
                  backgroundColor: ColorNames.Transparent,
                  content: (
                    <a
                      href={proofOfFundsDocumentURL}
                      target={isOnWebView ? '_self' : '_blank'}
                      type={proofOfFundsContentType}
                      download={isOnWebView}
                      data-elm-id='proof_of_funds_document_link'
                      onClick={() => {
                        instrument.domainEvent({
                          // @ts-ignore
                          type: graph.enums.DomainEventTypeName.LinkClicked,
                          source: listing?.urn,
                          data: {
                            element_type: 'link',
                            element_id: 'proof_of_funds_document_link',
                            intent: 'proof-of-funds-document-link-clicked',
                          },
                        })
                      }}
                    >
                      Proof Of Funds
                    </a>
                  ),
                },
              },
            ],
          },
        ],
      },
    ],
    [bidRank, listing, segment, viewingFrom, terms]
  )

  if (!isMobileOrTablet) {
    leftSections = [...leftSections, ...nonMobileFluidSections]
  }

  const buyerParty = getWinningBidUserData(closingMethod, segment)
  const buyerPhone = buyerParty?.phone_number
  const buyerEmail = buyerParty?.email_address

  const buyerInformationElmId = 'buyer-information'

  const buyerInformationSection: Section[] =
    viewingFrom !== graph.enums.CurrentTargetAudience.Buyer &&
    !!buyerParty &&
    bidRank?.bid_id === segment?.result?.winning_bid?.bid_id
      ? [
          {
            subsections: [
              {
                title: 'Buyer Information',
                className: cx('row-start-children'),
                children: [
                  {
                    label: 'Buyer Name:',
                    value: `${buyerParty?.first_name} ${buyerParty?.last_name}`,
                    width: '100%',
                    level: 3,
                    elmId: `${buyerInformationElmId}-buyer-name`,
                  },
                  {
                    label: 'Buyer Email:',
                    value: buyerEmail,
                    href: `mailto:${buyerEmail}`,
                    width: '100%',
                    level: 3,
                    elmId: `${buyerInformationElmId}-buyer-email`,
                  },
                  {
                    label: 'Buyer Phone:',
                    value: formatters.formatPhoneNumber(buyerPhone),
                    href: `tel:${buyerPhone}`,
                    width: '100%',
                    level: 3,
                    elmId: `${buyerInformationElmId}-buyer-phone`,
                  },
                ],
              },
            ],
          },
        ]
      : []

  const sellerRepresentative = findSellerRepresentative(
    listing?.parties || [],
    viewingFrom
  )

  const listingAgentSubSection: Section = {
    className: cx('listing-agent'),
    subsections: [
      {
        title: '',
        children: [
          {
            label: '',
            value: '',
            width: '100%',
            level: 3,
            type: 'LISTING_AGENT',
            viewingFrom,
            elmId: `listing-agent-field`,
          },
        ],
      },
    ],
  }

  const subSectionClassName = isMobileOrTablet
    ? 'row-children'
    : 'column-children'

  const paymentDetailsElmId = 'payment-details'
  const closingElmId = 'closing'
  const contingenciesElmId = 'contingencies'

  let rightSections = useMemo<Section[]>(
    () => [
      {
        subsections: [
          {
            title: 'Payment Details',
            className: cx(subSectionClassName),
            children: [
              {
                label: 'Earnest Money Deposit',
                value: valueOrDefault(
                  terms?.earnest_money_deposit_amount,
                  false,
                  `0`
                ),
                width: '50%',
                elmId: `${paymentDetailsElmId}-earnest-money-deposit`,
                level: 3,
                isMoneyValue: true,
                tooltipContent: `A deposit placed by the buyer to let the seller know that the buyer is earnest or serious about buying the property.`,
              },
              {
                label: 'Down payment',
                value: valueOrDefault(terms?.down_payment_amount, false, `0`),
                width: '50%',
                elmId: `${paymentDetailsElmId}-down-payment`,
                level: 3,
                isMoneyValue: true,
                tooltipContent: `A down payment is the portion of a property's purchase price that a buyer pays upfront when closing on a real estate loan.`,
              },
              {
                label: 'Payment Type',
                value: valueOrDefault(
                  (
                    _.find(traditionalOffer.paymentTypeOptions, {
                      value: terms?.payment_type,
                    }) as { text: string }
                  )?.text,
                  false,
                  'N/A'
                ),
                width: '50%',
                elmId: `${paymentDetailsElmId}-payment-type`,
                level: 3,
                tooltipContent: `The method in which the buyer intends to pay for the property.`,
              },
            ],
          },
          {
            title: '',
            children: [
              {
                label: '',
                value: '',
                width: '100%',
                level: 3,
                type: 'LINE_SEPARATOR',
                className: cx('line-separator'),
              },
            ],
          },
          {
            title: 'Closing',
            className: cx(subSectionClassName),
            children: [
              {
                label: 'Closing Date',
                value: valueOrDefault(
                  terms?.close_escrow_duration
                    ? `${terms?.close_escrow_duration} Days from Contract Execution`
                    : undefined
                ),
                width: '50%',
                elmId: `${closingElmId}-closing-date`,
                level: 3,
                tooltipContent: `The number of days agreed upon to complete the sale after the contract is signed.`,
              },
              {
                label: 'Rentback',
                value: valueOrDefault(
                  terms?.leaseback_duration
                    ? `${formatters.formatMoney(
                        terms?.leaseback_amount
                      )} per day for ${terms?.leaseback_duration} Days`
                    : undefined,
                  false,
                  'Not Offered'
                ),
                width: '50%',
                elmId: `${closingElmId}-rentback`,
                level: 3,
                tooltipContent: `The rentback period is the number of days the seller is requesting to stay in the property after the sale closes.`,
                hide:
                  !listing?.seller_terms?.leaseback_terms
                    ?.leaseback_period_in_days &&
                  !listing?.seller_terms?.leaseback_terms
                    ?.leaseback_period_rent,
              },
              {
                label: 'Seller Paid Closing Costs',
                value: valueOrDefault(sellerConcessionAmount, false, `0`),
                width: '50%',
                elmId: `${closingElmId}-seller-paid-closing-costs`,
                level: 3,
                isMoneyValue: true,
                tooltipContent: `The amount the buyer is requesting the seller to contribute towards the buyer's closing costs.`,
              },
              {
                label: 'Buyer’s Agent Commission',
                value: valueOrDefault(
                  buyerCommissionsAmount + sellerCommissionsAmount,
                  false,
                  `0`
                ),
                width: '50%',
                elmId: `${closingElmId}-buyers-agent-commission`,
                level: 3,
                isMoneyValue: true,
                tooltipContent: `The fee paid to the real estate agent who represents the buyer on the sale.`,
              },
            ],
          },
          {
            title: '',
            children: [
              {
                label: '',
                value: '',
                width: '100%',
                level: 3,
                type: 'LINE_SEPARATOR',
                className: cx('line-separator'),
              },
            ],
          },
          {
            title: 'Contingencies',
            className: cx(subSectionClassName),
            children: [
              {
                label: 'Inspection Contingency',
                value: valueOrDefault(
                  terms?.inspection_contingency_period_duration
                    ? `${terms?.inspection_contingency_period_duration} Days`
                    : undefined,
                  false,
                  'Not Requested'
                ),
                width: '50%',
                elmId: `${contingenciesElmId}-inspection-contingency`,
                level: 3,
                tooltipContent: `A provision allowing the buyer to have the property inspected and cancel the contract without penalty based on the inspection results.`,
                hide: !!listing?.seller_terms?.inspection_terms
                  ?.is_option_contingency,
              },
              {
                label: 'Option Period',
                value: valueOrDefault(
                  terms?.option_period_duration
                    ? `${
                        formatters.formatMoney(
                          terms?.option_period_cost_amount
                        ) as string
                      } for ${terms?.option_period_duration} Days`
                    : undefined,
                  false,
                  `Not Requested`
                ),
                width: '50%',
                elmId: `${contingenciesElmId}-option-period`,
                level: 3,
                tooltipContent: `The time during which buyers can conduct inspections and decide whether to move forward with the purchase. If the buyer does not proceed to closing, the Option Fee is retained by the seller.`,
                hide: !listing?.seller_terms?.inspection_terms
                  ?.is_option_contingency,
              },
              {
                label: 'Financing Contingency',
                value: valueOrDefault(
                  terms?.financing_contingency_duration
                    ? `${terms?.financing_contingency_duration} Days`
                    : undefined,
                  false,
                  'Not Requested'
                ),
                width: '50%',
                elmId: `${contingenciesElmId}-financing-contingency`,
                level: 3,
                tooltipContent: `A provision allowing the buyer to cancel the contract if they are unable to secure financing by a specified timeframe.`,
              },
              {
                label: 'Appraisal Contingency',
                value: valueOrDefault(
                  terms?.appraisal_contingency_duration
                    ? `${terms?.appraisal_contingency_duration} Days`
                    : undefined,
                  false,
                  `Not Requested`
                ),
                width: '50%',
                elmId: `${contingenciesElmId}-appraisal-contingency`,
                level: 3,
                tooltipContent: `A provision protecting the buyer by allowing them to cancel the contract if the property’s appraised value is lower than the agreed purchase price. `,
              },
            ],
          },
        ],
      },
      ...(viewingFrom === graph.enums.CurrentTargetAudience.Buyer &&
      segment?.current_user_bidding_status ===
        graph.enums.OnlineAuctionSegmentBiddingStatus.Won &&
      sellerRepresentative
        ? [listingAgentSubSection]
        : []),

      ...buyerInformationSection,
    ],
    [bidRank, listing, segment, viewingFrom, subSectionClassName]
  )

  if (isMobileOrTablet) {
    rightSections = [...rightSections, ...nonMobileFluidSections]
  }

  return (
    <div className={cx('section-container')}>
      <div key='left' className={cx('summary-section-container')}>
        {leftSections.map((s, i) => (
          <SummarySection
            key={i}
            subsections={s.subsections as any}
            listing={listing}
          />
        ))}
      </div>
      <div key='right' className={cx('summary-section-container')}>
        {rightSections.map((s, i) => (
          <SummarySection
            key={i}
            subsections={s.subsections as any}
            listing={listing}
            className={s.className ?? ''}
          />
        ))}
      </div>
    </div>
  )
}

export default BidRankSummary
